import React, {
  useContext,
  createRef,
  ChangeEvent,
  AriaAttributes,
} from 'react';
import c from 'classnames';
import ListCard from '../../../List/ListCard';
import { ListItemCheckbox } from '../../../List/ListItem';
import ListItemRadioButton from '../../../List/ListItem/dsPresets/ListItemRadioButton';
import { setRefs } from '../../../../utils/refHelpers';
import { DropdownStateContext } from '../../DropdownStateManager';
import { useStandardMenuKeyboardSupport } from '../../MenuPreset/StandardMenu/hooks';
import { DropdownOption } from '../../types';
import s from './MultiselectList.scss';

export interface Props {
  id: string; // for a11y
  items: DropdownOption[];
  className?: string;
  ariaLabel?: string;
  onChange?: (
    isSelected: boolean,
    value: string | number | undefined,
    e?: ChangeEvent<HTMLInputElement>
  ) => void;
  'aria-describedby'?: AriaAttributes['aria-describedby'];
  'aria-details'?: AriaAttributes['aria-details'];
  variant?: 'checkbox' | 'radio';
}

const MultiselectList: React.FC<Props> = ({
  id,
  items,
  className,
  ariaLabel,
  onChange,
  'aria-describedby': ariaDescribedby,
  variant = 'checkbox',
}) => {
  const menuRef = createRef<HTMLDivElement>();
  const { setMenuRef, closeDropdown } = useContext(DropdownStateContext);
  const { currentItemIndex, focusFirstItem, clearFocus, handleKeyboardEvents } =
    useStandardMenuKeyboardSupport(items);

  return (
    <ListCard
      onClick={e => e.stopPropagation()}
      id={id}
      className={className}
      role="listbox"
      aria-describedby={ariaDescribedby}
      aria-label={ariaLabel}
      tabIndex={0}
      innerRef={node => setRefs([setMenuRef, menuRef], node)}
      onKeyDown={e => {
        if (currentItemIndex !== null && e.code === 'Space') {
          const focusedItem = items[currentItemIndex];
          onChange && onChange(!focusedItem.selected, focusedItem.value);
          e.preventDefault();
        }
        handleKeyboardEvents(e);
      }}
      onBlur={e => {
        if (
          menuRef.current &&
          (!e.relatedTarget ||
            !menuRef.current.contains(e.relatedTarget as Node))
        ) {
          clearFocus();
        }
      }}
      onFocus={e => {
        if (e.target === menuRef.current) {
          focusFirstItem();
        }
      }}
    >
      {items.map((item, index) => {
        const indentClass =
          item.indentLevel && item.indentLevel > 0 && item.indentLevel <= 5
            ? s[`indent-${item.indentLevel}`]
            : undefined;
        const itemClassName = c(item.className, indentClass);

        return variant === 'radio' ? (
          <ListItemRadioButton
            key={index}
            name={id}
            className={itemClassName}
            label={item.label}
            value={`${item.value}`}
            checked={item.selected}
            suffix={item.suffix}
            onChange={
              onChange &&
              (isChecked => {
                onChange(isChecked, item.value);
                closeDropdown();
              })
            }
            disabled={item.disabled}
          />
        ) : (
          <ListItemCheckbox
            key={index}
            className={itemClassName}
            label={item.label}
            value={`${item.value}`}
            checked={item.selected}
            focused={index === currentItemIndex}
            suffix={item.suffix}
            onChange={
              onChange && ((isChecked, e) => onChange(isChecked, item.value, e))
            }
            disabled={item.disabled}
          />
        );
      })}
    </ListCard>
  );
};
MultiselectList.displayName = 'MultiselectList';

export default MultiselectList;
